import { useState, useEffect } from 'react'
import { Outlet, useLocation, Link } from 'react-router-dom'
import { Menu, X, Search } from 'lucide-react'
import { AppSidebar } from './app-sidebar'
import { CommandMenu } from './command-menu'
import { ThemeToggle } from './theme-toggle'
import { Button } from '@/components/ui/button'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { ErrorBoundary } from '@/components/ErrorBoundary'

// Breadcrumb mapping
const breadcrumbMap: Record<string, string> = {
  '/admin': 'Dashboard',
  '/admin/maengel': 'Mängel',
  '/admin/bauvorhaben': 'Bauvorhaben',
  '/admin/bautagebuch': 'Bautagebücher',
  '/admin/bestellungen': 'Bestellungen',
  '/admin/produkte': 'Produkte',
  '/admin/geraete': 'Geräte & Maschinen',
  '/admin/mitarbeiter': 'Mitarbeiter',
  '/admin/zeiterfassung': 'Zeiterfassung',
  '/admin/krankmeldungen': 'Krankmeldungen',
  '/admin/urlaub': 'Urlaub',
  '/admin/stundenzettel': 'Stundenzettel',
  '/admin/buchhaltung': 'Buchhaltung',
  '/admin/ausgangsrechnungen': 'Ausgangsrechnungen',
  '/admin/dokumentenablage': 'Dokumentenablage',
  '/admin/kalkulation': 'Kalkulation',
  '/admin/nachrichten': 'Nachrichten',
  '/admin/berichte': 'Berichte',
  '/admin/ki': 'K.I.',
  '/admin/bug-tracker': 'Bug Tracker',
  '/admin/benutzerverwaltung': 'Benutzerverwaltung',
  '/admin/einstellungen': 'Einstellungen',
  '/admin/uebersicht': 'Übersicht',
}

function getBreadcrumbs(pathname: string) {
  // Exact match
  if (breadcrumbMap[pathname]) {
    return [{ label: breadcrumbMap[pathname], path: pathname }]
  }

  // Detail pages
  const segments = pathname.split('/').filter(Boolean)
  const crumbs: { label: string; path?: string }[] = []

  if (segments.length >= 3) {
    const parentPath = '/' + segments.slice(0, 2).join('/')
    if (breadcrumbMap[parentPath]) {
      crumbs.push({ label: breadcrumbMap[parentPath], path: parentPath })
    }
    // Sub-paths
    if (segments[1] === 'kalkulation' && segments[2] === 'datanorm') {
      crumbs.push({ label: 'Datanorm-Suche' })
    } else if (segments[1] === 'ki' && segments[2] === 'zaehler') {
      crumbs.push({ label: 'Zaehler' })
    } else if (segments[1] === 'berichte' && segments[2] === 'bauvorhaben') {
      crumbs.push({ label: 'Bauvorhaben-Statistiken' })
    } else if (segments[1] === 'controlling') {
      crumbs.length = 0
      crumbs.push({ label: 'Kalkulation', path: '/admin/kalkulation' })
      crumbs.push({ label: 'Controlling' })
    } else {
      crumbs.push({ label: 'Detail' })
    }
  }

  return crumbs
}

// AI Status Indicator
function AiStatusBanner() {
  const [status, setStatus] = useState<{
    status: string
    incident?: { name: string }
  } | null>(null)

  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch('/api/ai-status')
        const data = await res.json()
        setStatus(data)
      } catch {
        setStatus(null)
      }
    }
    check()
    const interval = setInterval(check, 60000)
    return () => clearInterval(interval)
  }, [])

  if (!status || status.status === 'operational') return null

  const config: Record<string, { bg: string; text: string; dot: string; label: string }> = {
    partial_outage: { bg: 'bg-amber-50 border-amber-200 dark:bg-amber-950 dark:border-amber-800', text: 'text-amber-800 dark:text-amber-200', dot: 'bg-amber-400', label: 'Teilweise gestoert' },
    major_outage: { bg: 'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800', text: 'text-red-800 dark:text-red-200', dot: 'bg-red-500', label: 'Gestoert' },
    degraded_performance: { bg: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950 dark:border-yellow-800', text: 'text-yellow-800 dark:text-yellow-200', dot: 'bg-yellow-400', label: 'Eingeschraenkt' },
  }
  const c = config[status.status] || config.partial_outage

  return (
    <div className={`${c.bg} border rounded-lg px-4 py-2.5 mb-4 flex items-center gap-3 text-sm`}>
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${c.dot} opacity-75`} />
        <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${c.dot}`} />
      </span>
      <div className={c.text}>
        <strong>KI-Dienst: {c.label}</strong>
        {status.incident && <span className="ml-2 font-normal">— {status.incident.name}</span>}
        <span className="ml-2 font-normal opacity-75">
          Dokument-Analyse und Erkennung koennen verzoegert sein oder fehlschlagen.
        </span>
      </div>
      <a
        href="https://status.claude.com"
        target="_blank"
        rel="noopener noreferrer"
        className={`${c.text} underline opacity-75 hover:opacity-100 ml-auto whitespace-nowrap shrink-0`}
      >
        Status-Seite
      </a>
    </div>
  )
}

export function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()

  // Close sidebar on navigation (mobile)
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  // Prevent body scroll when sidebar is open (mobile)
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [sidebarOpen])

  const breadcrumbs = getBreadcrumbs(location.pathname)

  return (
    <div className="min-h-screen bg-background">
      {/* Command Menu (Cmd+K) */}
      <CommandMenu />

      {/* Mobile Header */}
      <div className="fixed left-0 right-0 top-0 z-30 flex h-14 items-center bg-sidebar px-4 shadow-lg lg:hidden">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="-ml-1.5 rounded-lg p-1.5 text-sidebar-foreground transition-colors hover:bg-sidebar-accent"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
        <div className="ml-3 flex items-center gap-2">
          <span className="text-lg font-bold text-sidebar-foreground">COMPEER</span>
          <span className="text-xs text-sidebar-foreground/60">Bau-Management</span>
        </div>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-0 z-40 h-full w-64 transform overflow-hidden transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <AppSidebar onClose={() => setSidebarOpen(false)} />
      </div>

      {/* Main Content */}
      <main className="min-h-screen pt-14 lg:ml-64 lg:pt-0">
        {/* Page Header */}
        <header className="hidden lg:flex items-center justify-between border-b bg-card px-6 py-3">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/admin">Dashboard</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {breadcrumbs.map((crumb, i) => (
                <span key={i} className="contents">
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    {i === breadcrumbs.length - 1 ? (
                      <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link to={crumb.path!}>{crumb.label}</Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </span>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="hidden md:flex items-center gap-2 text-muted-foreground"
              onClick={() => {
                document.dispatchEvent(
                  new KeyboardEvent('keydown', { key: 'k', metaKey: true })
                )
              }}
            >
              <Search className="h-4 w-4" />
              <span>Suche...</span>
              <kbd className="pointer-events-none ml-2 inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                <span className="text-xs">Ctrl</span>K
              </kbd>
            </Button>
            <ThemeToggle />
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:p-8">
          {location.pathname.includes('dokumentenablage') && <AiStatusBanner />}
          <ErrorBoundary key={location.pathname} inline>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>

      {/* Document Chat Bubble — nur in Dokumentenablage sichtbar */}
      {/* Dokumenten-Chat seit 21.10.2026 auf Eis (schickte die ganze Ablage an die Claude-API, DSGVO) — Neubau als lokales RAG */}
    </div>
  )
}
